  import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
  import axios, { AxiosError } from 'axios';
  import {
    StateSchema,
    ThunkConfig,
    ChatLog,
    SessionHistory,
    RequestStatuses,
    ToolType,
    BrowserAgentLog,
    IsBrowserStepsLoading,
    SavedNetworkPayload,
    NetworkLog,
  } from '../stateSchema';
  import { BACKEND_URL } from '../store';
  import {
    checkAuthorization,
    getCurrentUser,
    getToken,
    logout,
  } from './authSlice';
  import {
    addUserMessageCount,
    setIsPaymentCardVisible,
    setSpecialLimitApplies,
  } from './subscriptionSlice';
  import { wsAPI } from '../../providers/ws-api';
  import { toast } from 'react-toastify';
  import { LocalStorageKeys } from '../../utils/types';
  import { getSessionsWithSelectedTool } from '../../utils/helpers';

  const chatSlice = createSlice({
    name: 'chat',
    initialState: {
      chatLog: [] as ChatLog[],
      browserAgentLog: [] as BrowserAgentLog[],
      currentSessionId: Number(localStorage.getItem('chat_session')) || 0,
      sessionHistory: [] as SessionHistory[],
      sessionsWithBlockedUserPrompt: [] as number[],
      isWebSocketOpen: false,
      fetchChatSessionHistoryStatus: RequestStatuses.IDLE,
      sessionsWithSelectedTool: getSessionsWithSelectedTool(),
      isBrowserVisible: false,
      isBrowserStepsLoading: [] as IsBrowserStepsLoading[],
      toolStatus: "",
    },
    reducers: {
      setChatLog: (state, action: PayloadAction<ChatLog[]>) => {
        state.chatLog = action.payload;

        const isSessionBlocked = () =>
          state.sessionsWithBlockedUserPrompt.includes(state.currentSessionId);

        if (
          !isSessionBlocked() &&
          state.chatLog[state.chatLog.length - 1]?.role === 'user'
        ) {
          if (!state.sessionsWithBlockedUserPrompt.includes(state.currentSessionId)) {
            state.sessionsWithBlockedUserPrompt.push(state.currentSessionId);
          }
        }

        if (isSessionBlocked() && !state.isBrowserVisible) {
          state.chatLog.push({
            role: 'assistant',
            message: 'Running...',
            timestamp: new Date().toUTCString(),
            chat_session: state.currentSessionId,
            id: 0,
          });
        }
      },
      setBrowserAgentLog: (state, action: PayloadAction<BrowserAgentLog[]>) => {
        state.browserAgentLog = action.payload;
      },
      addBrowserAgentLog: (state, action: PayloadAction<BrowserAgentLog>) => {
        const lastIndex = state.browserAgentLog.length - 1;
      
        if (
          lastIndex >= 0 &&
          state.browserAgentLog[lastIndex].url === action.payload.url
        ) {
          // Replace the last entry
          state.browserAgentLog[lastIndex] = action.payload;
        } else {
          // Add as new entry
          state.browserAgentLog.push(action.payload);
        }
      },
      addUserMessageToChatLog: (state, action: PayloadAction<string>) => {
        const newChatLogEntry: ChatLog[] = [
          {
            role: 'user',
            message: action.payload,
            timestamp: new Date().toUTCString(),
            chat_session: state.currentSessionId,
            id: 0,
          },
          {
            role: 'assistant',
            message: 'Running...',
            timestamp: new Date().toUTCString(),
            chat_session: state.currentSessionId,
            id: 0,
          },
        ];

        state.chatLog.push(...newChatLogEntry);

        const historyIdx = state.sessionHistory.findIndex(
          (sh) => sh.id === state.currentSessionId
        );

        if (
          historyIdx !== -1 &&
          !state.sessionHistory[historyIdx].first_message
        ) {
          state.sessionHistory[historyIdx].first_message = action.payload;
        }

        if (!state.sessionsWithBlockedUserPrompt.includes(state.currentSessionId)) {
          state.sessionsWithBlockedUserPrompt.push(state.currentSessionId);
        }
      },
      addWSMessageToChatLog: (state, action: PayloadAction<ChatLog>) => {
        if (state.currentSessionId === action.payload.chat_session) {
          state.chatLog = state.chatLog.filter((entry) => entry.message !== 'Running...');

          const existingIndex = state.chatLog.findIndex(
            (entry) => entry.id === action.payload.id
          );
          
          if (existingIndex !== -1) {
            // Just update the message for that ID
            state.chatLog[existingIndex] = {
              ...state.chatLog[existingIndex],
              message: action.payload.message,
            };
          } else {
            // Otherwise add new message
            state.chatLog.push(action.payload);
          }
        } else {
          // add badge "new message" for corresponding session
        }
        
        // Only unblock if message is not a blocked agent task
        // const matchedLogEntry = state.chatLog.find(
        //   (entry) => entry.id === action.payload.id
        // );
        
        // Clear blocked prompt if it was triggered
        state.sessionsWithBlockedUserPrompt = state.sessionsWithBlockedUserPrompt.filter(
          (id) => id !== action.payload.chat_session
        );
      },    
      setCurrentSessionId: (state, action: PayloadAction<number>) => {
        state.currentSessionId = action.payload;
        localStorage.setItem('chat_session', action.payload.toString());
      },
      setSessionHistory: (state, action: PayloadAction<SessionHistory[]>) => {
        state.sessionHistory = action.payload;
      },
      addChatSession: (state, action: PayloadAction<SessionHistory>) => {
        state.sessionHistory.unshift(action.payload);
      },
      removeChatSession: (state, action: PayloadAction<number>) => {
        state.sessionHistory = state.sessionHistory.filter(
          (sh) => sh.id !== action.payload
        );
      },
      setToolStatus: (state, action: PayloadAction<string>) => {
        state.toolStatus = action.payload;
      },
      resetSessionsWithBlockedUserPrompt: (state) => {
        state.sessionsWithBlockedUserPrompt = [];
      },
      removeSessionFromBlockedUserPrompt: (state, action: PayloadAction<number>) => {
        state.sessionsWithBlockedUserPrompt = state.sessionsWithBlockedUserPrompt.filter(
          (id) => id !== action.payload
        );
      },
      addSessionWithBlockedUserPrompt: (state, action) => {
        const sessionId = action.payload;
        if (!state.sessionsWithBlockedUserPrompt.includes(sessionId)) {
          state.sessionsWithBlockedUserPrompt.push(sessionId);
        }
      },
      setIsWebSocketOpen: (state, action: PayloadAction<boolean>) => {
        state.isWebSocketOpen = action.payload;
      },
      toggleSessionSelectedTool: (state, action: PayloadAction<ToolType>) => {
        if (action.payload === ToolType.BLANK) {
          delete state.sessionsWithSelectedTool[state.currentSessionId];
        } else {
          state.sessionsWithSelectedTool[state.currentSessionId] = action.payload;
        }

        localStorage.setItem(
          LocalStorageKeys.SESSIONS_WITH_SELECTED_TOOL,
          JSON.stringify(state.sessionsWithSelectedTool)
        );
      },
      setIsBrowserVisible: (state, action: PayloadAction<boolean>) => {
        state.isBrowserVisible = action.payload;
      },
      setIsBrowserStepsLoading: (state,  action: PayloadAction<{ message_id: number; isLoading: boolean }>) => {
        const existingIndex = state.isBrowserStepsLoading.findIndex(
          (entry) => entry.message_id === action.payload.message_id
        );
      
        if (existingIndex !== -1) {
          state.isBrowserStepsLoading[existingIndex].isLoading = action.payload.isLoading;
        } else {
          state.isBrowserStepsLoading.push(action.payload);
        }
      }
      
    },
    extraReducers(builder) {
      builder
        .addCase(fetchChatSessionHistory.pending, (state) => {
          state.fetchChatSessionHistoryStatus = RequestStatuses.PENDING;
        })
        .addCase(fetchChatSessionHistory.fulfilled, (state) => {
          state.fetchChatSessionHistoryStatus = RequestStatuses.SUCCEEDED;
        })
        .addCase(fetchChatSessionHistory.rejected, (state) => {
          state.fetchChatSessionHistoryStatus = RequestStatuses.FAILED;
        });
    },
  });

  export const {
    setChatLog,
    setBrowserAgentLog,
    addBrowserAgentLog,
    addUserMessageToChatLog,
    addWSMessageToChatLog,
    setCurrentSessionId,
    setSessionHistory,
    addChatSession,
    removeChatSession,
    resetSessionsWithBlockedUserPrompt,
    removeSessionFromBlockedUserPrompt,
    addSessionWithBlockedUserPrompt,
    setIsWebSocketOpen,
    toggleSessionSelectedTool,
    setIsBrowserVisible,
    setIsBrowserStepsLoading,
    setToolStatus,
  } = chatSlice.actions;

  export const chatReducer = chatSlice.reducer;

  export const getChatLog = (state: StateSchema) => state.chat.chatLog;

  export const getBrowserAgentLog = (state: StateSchema) => state.chat.browserAgentLog;

  export const getIsBrowserVisible = (state: StateSchema) => state.chat.isBrowserVisible;

  export const getIsBrowserStepsLoading = (state: StateSchema) => state.chat.isBrowserStepsLoading;

  export const getSessionsWithBlockedUserPrompt = (state: StateSchema) => state.chat.sessionsWithBlockedUserPrompt;

  export const getCurrentSessionId = (state: StateSchema) =>
    state.chat.currentSessionId;

  export const getIsBrowserSession = (sessionId: number) => (state: StateSchema) =>
    state.chat.sessionHistory.find(
      (sessionHistory) => sessionHistory.id === sessionId
    )?.is_browser || false;

  export const getSessionHistory = (state: StateSchema) =>
    state.chat.sessionHistory;

  export const getPromptInputIsBlocked = (state: StateSchema) =>
    state.chat.sessionsWithBlockedUserPrompt.includes(
      state.chat.currentSessionId
    );

  export const getToolStatus = (state: StateSchema) =>
    state.chat.toolStatus;

  export const getIsWebSocketOpen = (state: StateSchema) =>
    state.chat.isWebSocketOpen;

  export const getSessionSelectedTool = (state: StateSchema): ToolType | null =>
    state.chat.sessionsWithSelectedTool[state.chat.currentSessionId] || null;

  export const fetchChatLog = createAsyncThunk<unknown, void, ThunkConfig>(
    'chat/fetchChatLog',
    async (_, { getState, dispatch }) => {
      try {
        const sessionId = getCurrentSessionId(getState());

        if (sessionId) {
          const response = await axios.get<{ messages: ChatLog[], browser_agent_logs: BrowserAgentLog[] }>(
            `${BACKEND_URL}/chat/chat-history/${sessionId}/`,
            {
              headers: {
                Authorization: `Bearer ${getToken(getState())}`,
              },
            }
          );

          if (!response.data) {
            throw Error();
          }

          // Append loading placeholder if needed
          const lastMsg = response.data?.messages.at(-1);
          
          // Unblock if last message is from assistant and not the placeholder we just added
          const shouldUnblock =
            lastMsg?.role === "assistant" &&
            (lastMsg?.message !== "Running...");

          if (shouldUnblock) {
            dispatch(removeSessionFromBlockedUserPrompt(sessionId));
          }

          dispatch(
            setChatLog(response.data?.messages)
          );
          dispatch(
            setBrowserAgentLog(
              response.data.browser_agent_logs
            )
          )
          if (response.data?.browser_agent_logs?.length > 0) {
            dispatch(setIsBrowserVisible(true));
          } else {
            dispatch(setIsBrowserVisible(false));
          }
        }
      } catch (error: unknown) {
        // setErr(true);
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.status === 401
        ) {
          dispatch(logout());
        }
      }
    }
  );

  export const fetchChatSessionHistory = createAsyncThunk<
    unknown,
    { force?: boolean } | void,
    ThunkConfig
  >('chat/fetchChatSessionHistory', async (_, { getState, dispatch }) => {
    try {
      const response = await axios.get<SessionHistory[]>(
        `${BACKEND_URL}/chat/sessions-history/`,
        {
          headers: {
            Authorization: `Bearer ${getToken(getState())}`,
          },
        }
      );
      if (response.data.length > 0) {
        dispatch(setSessionHistory(response.data));

        if (
          !response.data.find((sh) => sh.id === getCurrentSessionId(getState()))
        ) {
          dispatch(setCurrentSessionId(getSessionHistory(getState())[0].id));
        } else if (
          getCurrentSessionId(getState()) === 0 &&
          getSessionHistory(getState()).length > 0
        ) {
          dispatch(setCurrentSessionId(getSessionHistory(getState())[0].id));
        }
      } else {
        dispatch(createChatSession());
      }
    } catch (error: unknown) {
      // setErr(true);
      if (
        axios.isAxiosError(error) &&
        error.response &&
        error.response.status === 401
      ) {
        dispatch(logout());
      }
    }
  }, {
    // Condition for deduplicating initial request
    condition(arg, thunkApi) {
      const fetchChatSessionHistoryStatus = thunkApi.getState().chat.fetchChatSessionHistoryStatus;
      const force = typeof arg === 'object' && arg?.force;
      if (!force && fetchChatSessionHistoryStatus !== RequestStatuses.IDLE) {
        return false;
      }
    }
  });

  export const createChatSession = createAsyncThunk<unknown, void, ThunkConfig>(
    'chat/createChatSession',
    async (_, { getState, dispatch }) => {
      try {
        const response = await axios.post(
          `${BACKEND_URL}/chat/session/create/`,
          {},
          {
            headers: {
              Authorization: `Bearer ${getToken(getState())}`,
            },
          }
        );
        dispatch(setChatLog([]));
        dispatch(addChatSession(response.data));
        dispatch(setCurrentSessionId(response.data.id));
      } catch (error: unknown) {
        // Ensure 'error' is an AxiosError before accessing response properties
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<{ detail?: string }>;

          if (axiosError.response) {
            const { status, data } = axiosError.response;

            if (status === 401) {
              dispatch(logout());
            } else if (status === 403) {
              toast.error(data?.detail || 'You are not authorized to create a chat session.', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
              });
            }
          }
        } else {
          // Handle unknown errors safely
          toast.error('An unexpected error occurred. Please try again.', {
            position: 'top-right',
            autoClose: 5000,
          });
        }
      }
    }
  );

  export const deleteChatSession = createAsyncThunk<unknown, number, ThunkConfig>(
    'chat/deleteChatSession',
    async (sessionId, { getState, dispatch }) => {
      try {
        await axios.delete(`${BACKEND_URL}/chat/chat-history/${sessionId}/`, {
          headers: {
            Authorization: `Bearer ${getToken(getState())}`,
          },
        });
        dispatch(setChatLog([]));
        dispatch(removeChatSession(sessionId));

        const sessionHistory = getSessionHistory(getState());

        if (sessionHistory.length > 0) {
          dispatch(setCurrentSessionId(sessionHistory[0].id));
        } else {
          dispatch(createChatSession());
        }
      } catch (error: unknown) {
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.status === 401
        ) {
          dispatch(logout());
        }
      }
    }
  );

  export const saveNetworkPayload = createAsyncThunk<
    SavedNetworkPayload,
    {
      original_network_log: any;
      updated_network_log: any;
      label?: string;
      description?: string;
      source_log_id?: number;
      step_id?: number;
    },
    ThunkConfig
  >('chat/saveNetworkPayload', async (data, { getState, rejectWithValue }) => {
    try {
      const sessionId = getCurrentSessionId(getState());
      const response = await axios.post(
        `${BACKEND_URL}/chat/saved-payloads/`,
        {
          chat_session_id: sessionId,
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken(getState())}`,
          },
        }
      );

      return response.data as SavedNetworkPayload;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ detail: 'Unknown error saving payload.' });
    }
  });

  export const updateNetworkPayload = createAsyncThunk<
    SavedNetworkPayload,
    {
      id: number;
      updated_network_log: any;
      label?: string;
      description?: string;
    },
    ThunkConfig
  >('chat/updateNetworkPayload', async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/chat/saved-payloads/${data.id}/`,
        {
          updated_network_log: data.updated_network_log,
          label: data.label,
          description: data.description,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken(getState())}`,
          },
        }
      );
    
      return response.data as SavedNetworkPayload;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ detail: 'Unknown error updating payload.' });
    }
  });

  export const fetchSavedPayloads = createAsyncThunk<
    SavedNetworkPayload[], // return type
    number,                // chatSessionId
    ThunkConfig
  >(
    'chat/fetchSavedPayloads',
    async (chatSessionId, { getState, rejectWithValue }) => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/chat/saved-payloads/?chat_session=${chatSessionId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken(getState())}`,
            },
          }
        );

        return response.data as SavedNetworkPayload[];
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ detail: 'Failed to fetch saved payloads.' });
      }
    }
  );

  export const replayNetworkRequest = createAsyncThunk<
    NetworkLog,
    NetworkLog,
    ThunkConfig
  >('chat/replayNetworkRequest', async (networkLog, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/chat/saved-payloads/replay/`,
        networkLog,
        {
          headers: {
            Authorization: `Bearer ${getToken(getState())}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const updatedResponse = {
        ...networkLog,
        response: response.data.response,
        status: response.data.status,
        headers: response.data.headers,
        responseBody: response.data.body,
      };

      return updatedResponse;
    } catch (error) {
      console.error('Replay request failed:', error);
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ detail: 'Replay failed.' });
    }
  });

  export const connectWebSocket = createAsyncThunk<unknown, void, ThunkConfig>(
    'chat/connectWebSocket',
    async (_, { dispatch, getState }) => {
      const handleCheckAuth = (): void => {
        dispatch(checkAuthorization());
      };

      const handleSendMessage = (message: string): void => {
        const data = JSON.parse(message);
       
        let parsedResponse: any = data.response;

        if (getCurrentSessionId(getState()) !== data.session_id) {
          return
        }
        // Try to parse response only if it looks like a JSON object
        if (typeof data.response === 'string' && data.response.trim().startsWith('{')) {
          try {
            parsedResponse = JSON.parse(data.response);
          } catch (err) {
            // Not JSON, treat as plain string (the existing format)
            parsedResponse = data.response;
          }
        }

        // Handle different types of responses
        if (parsedResponse?.type === 'browser_function_status') {
          // Handle browser function status update
          dispatch(setToolStatus(`${parsedResponse?.function}: ${parsedResponse?.instruction}`));
        } else if (parsedResponse?.type === 'browser_agent_details') {
          // Handle browser agent log response
          dispatch(addBrowserAgentLog({
            url: parsedResponse?.url,
            title: parsedResponse?.title,
            image_url: parsedResponse?.image_url,
            session_id: data.session_id,
            network_logs: parsedResponse?.network_logs,
          }));
          dispatch(setIsBrowserVisible(true));
        } else {
          // Handle regular chat response
          const chatLogEntry: ChatLog = {
            role: 'assistant',
            message: parsedResponse || '',
            timestamp: new Date().toUTCString(),
            chat_session: data?.session_id,
            id: data?.last_message,
          };
          dispatch(addWSMessageToChatLog(chatLogEntry));
          dispatch(addUserMessageCount(1));
          // If the response contains a free limit exceeded message, show payment card
          if (parsedResponse?.includes?.('exceeded your free limit')) {
            dispatch(setIsPaymentCardVisible(true));
            dispatch(setSpecialLimitApplies(true));
          }
          // Clear tool status after message is processed
          dispatch(setToolStatus(""));
          // Remove session from blocked user prompt if it was unblocked
          dispatch(removeSessionFromBlockedUserPrompt(data?.session_id))
        }
      };
      
      const handleStatusChange = (status: boolean): void => {
        dispatch(resetSessionsWithBlockedUserPrompt());
        dispatch(setIsWebSocketOpen(status));
      };

      wsAPI.start(handleCheckAuth, handleSendMessage, handleStatusChange);
    }
  );

  export const closeWebSocket = createAsyncThunk<unknown, void, ThunkConfig>(
    'chat/closeWebSocket',
    async () => {
      wsAPI.stop();
    }
  );

  export const sendMessage = createAsyncThunk<unknown, { message: string }, ThunkConfig>(
    'chat/sendMessage', async ({ message }, { getState, dispatch }) => {
    const state = getState();
    const sessionId = getCurrentSessionId(state);
    const userId = getCurrentUser(state);
    let selectedTool = getSessionSelectedTool(state) || ToolType.BLANK;

    if (state.chat.isBrowserVisible) {
      selectedTool = ToolType.BROWSER;
    }
    
    dispatch(addUserMessageToChatLog(message));

    const userMessage = {
      message,
      user_id: userId,
      session_id: sessionId,
      tool: selectedTool,
      browser_height: window.outerHeight
    };

    wsAPI.sendMessage(userMessage);
  });
